import { useState } from 'react'
import { ApiError, api } from '../api/client'
import type { Station } from '../api/types'
import { collectLine, sourceNote } from '../lib/gatewayRounds'
import { usePolled } from '../lib/usePolled'
import { formatTime } from '../lib/time'

/** How often the line under the button is refreshed: often while a request
 *  is on its way, rarely otherwise. */
const BUSY_POLL_MS = 5_000
const IDLE_POLL_MS = 60_000

type Props = {
  station: Station
}

/** Collect now (db/037): ask the intake to read this station at once, for
 *  one that is very late or has just come back. Once per station per ten
 *  minutes whoever asks, enforced by the server; the button shows when it
 *  may be pressed again and what the latest request did. */
export function CollectNow({ station }: Props) {
  const [busyPoll, setBusyPoll] = useState(false)
  const [asking, setAsking] = useState(false)
  const [refusal, setRefusal] = useState<string>()
  const status = usePolled(() => api.collectStatus(station.code), [station.code],
                           busyPoll ? BUSY_POLL_MS : IDLE_POLL_MS)
  const current = status.data
  const working = current?.latest?.status === 'pending' || current?.latest?.status === 'running'
  if (working !== busyPoll) setBusyPoll(working)

  if (!current || !current.available) return null

  async function handleClick() {
    setAsking(true)
    setRefusal(undefined)
    try {
      await api.collectNow(station.code)
      setBusyPoll(true)
    } catch (cause) {
      setRefusal(cause instanceof ApiError ? cause.message : 'The request did not go through.')
    } finally {
      setAsking(false)
      status.reload()
    }
  }

  const againAt = current.again_at ? formatTime(current.again_at) : null
  const line = collectLine(current)
  const note = sourceNote(current)
  return (
    <div className="collect-now">
      {note && <p className="collect-now-line">{note}</p>}
      <button type="button" className="button" onClick={handleClick}
              disabled={asking || working || !current.can_request}
              title="Read everything the station holds since its newest stored reading. Once per station every ten minutes.">
        {asking ? 'Asking…' : 'Collect now'}
      </button>
      {againAt && !working && <span className="muted">Again at {againAt}</span>}
      {line && <p className="collect-now-line" role="status">{line}</p>}
      {refusal && <p className="error">{refusal}</p>}
    </div>
  )
}
